// components/WritingArticle.jsx
import React, { useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import rehypeSlug from "rehype-slug";
import rehypeAutolinkHeadings from "rehype-autolink-headings";
import "katex/dist/katex.min.css"; // KaTeX styles
import "../styles/WritingArticle.css";

const WritingArticle = ({ title, authors = [], date, content, pdfUrl }) => {
  // Scroll to reference when clicking footnote link
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash) {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    };

    handleHashChange(); // Check on mount
    window.addEventListener("hashchange", handleHashChange);

    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  return (
    <>
      <title>{title} - Arthur Efremenko</title>
      <meta name="description" content={title} />
      <article className="writing-article">
        <header className="article-header">
          <h1 className="article-title">{title}</h1>

          {authors.length > 0 && (
            <div className="article-authors">
              {authors.map((author, index) => (
                <span key={index} className="author-name">
                  {author.url ? (
                    <a
                      href={author.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="author-link"
                    >
                      {author.name}
                    </a>
                  ) : (
                    author.name
                  )}
                  {author.affiliation && (
                    <span className="author-affiliation">
                      <sup>{author.affiliation}</sup>
                    </span>
                  )}
                  {index < authors.length - 1 && (
                    <span className="author-separator">,</span>
                  )}
                </span>
              ))}
            </div>
          )}

          {date && <time className="article-date">{date}</time>}
        </header>

        <div className="article-content">
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkMath]}
            rehypePlugins={[rehypeKatex, rehypeSlug, rehypeAutolinkHeadings]}
            components={{
              // Custom link rendering for references
              a: ({ node, children, href, ...props }) => {
                // Check if it's a reference link (starts with #)
                if (href && href.startsWith("#")) {
                  return (
                    <a href={href} className="reference-link" {...props}>
                      {children}
                    </a>
                  );
                }
                // External links
                return (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    {...props}
                  >
                    {children}
                  </a>
                );
              },
              // Style for reference definitions
              li: ({ node, children, ...props }) => {
                const text = node.children[0]?.value || "";
                // Check if this is a reference (starts with ^)
                if (text.startsWith("^")) {
                  return (
                    <li className="reference-item" {...props}>
                      {children}
                    </li>
                  );
                }
                return <li {...props}>{children}</li>;
              },
            }}
          >
            {content}
          </ReactMarkdown>
        </div>

        {pdfUrl && (
          <div className="pdf-download-section">
            <a
              href={pdfUrl}
              download
              className="pdf-download-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Download Full Paper (PDF)
            </a>
          </div>
        )}
      </article>
    </>
  );
};

export default WritingArticle;
